import type { FlickingOptions, Plugin } from '@egjs/flicking'
import type Flicking from '@egjs/flicking'
import { FLICKING_BULLET_ANIMATION_NAME } from '../../settings/tailwind'
import { getFlickingPlugin } from '~/helpers/flicking/plugins'

type WithFlickingCallback = (flicking: Flicking) => void

type UseFlicking = {
  /**
   * Run a callback with a Flicking instance.
   *
   * If the flicking instance is not available, the callback is not executed.
   */
  withFlicking: (cb: WithFlickingCallback) => void

  /**
   * Move to a specific index.
   */
  moveTo: (index: number, duration?: number) => void

  /**
   * Go to the next slide.
   */
  next: () => void
}

type UseFlickingOptions = {
  /**
   * Options that are passed to Flicking.
   */
  flickingOptions?: Partial<FlickingOptions>

  /**
   * Plugins to initialise.
   */
  plugins?: (
    flicking: Flicking,
  ) => Plugin | Plugin[] | Promise<Plugin | Plugin[]>

  /**
   * Use the custom autoplay-pagination.
   */
  useAutoplayPagination?: boolean

  /**
   * Whether flicking is enabled.
   */
  isEnabled?: ComputedRef<boolean> | Ref<boolean>

  /**
   * If set, this key is used to persist the slider position when the component
   * is unmounted. If a component with the same key is mounted again later, the
   * slider is initialised at the stored index.
   *
   * The index is only persisted in memory, so a refresh removes all entries.
   * This is primarily used when clicking on a teaser in a slider and then
   * going back to the previous page. That way the user is in the same spot
   * inside the slider.
   */
  persistKey?: ComputedRef<string | null | undefined>
}

export async function useFlicking(
  el: Ref<HTMLElement | null | undefined>,
  options?: UseFlickingOptions,
): Promise<UseFlicking> {
  let flicking: Flicking | null = null
  // @TODO: Prevent duplicate initialisation for same element.
  let _initialisedForElement: HTMLElement | null = null

  // @TODO: Figure out weird bug with Pagination plugin where sometimes
  // sliding with mouse or touch stops working.
  // It has to be this plugin, because removing it fixes the issue.

  const currentIndexState = useState<Record<string, number>>(
    'flickingCurrentIndex',
    () => {
      return {}
    },
  )

  const persistKey = computed(() => options?.persistKey?.value)

  const isEnabled = computed(() => {
    if (options && options.isEnabled) {
      return options.isEnabled.value
    }

    return true
  })

  async function initFlicking() {
    if (!el.value || !isEnabled.value) {
      return
    }

    _initialisedForElement = el.value

    const { default: Flicking } = await import('@egjs/flicking')

    const defaultIndex = persistKey.value
      ? currentIndexState.value[persistKey.value]
      : null

    const flickingOptions = options?.flickingOptions || {}
    flicking = new Flicking(el.value, {
      ...flickingOptions,
      defaultIndex: defaultIndex || undefined,
      noPanelStyleOverride: true,
    })

    // initialise plugins.
    const pluginsUser = options?.plugins ? await options.plugins(flicking) : []
    const plugins = Array.isArray(pluginsUser) ? pluginsUser : [pluginsUser]
    flicking.addPlugins(...plugins)

    if (options?.useAutoplayPagination) {
      const Pagination = await getFlickingPlugin('Pagination')

      flicking.addPlugins(
        new Pagination({
          type: 'bullet',
          renderBullet: () =>
            `<button class="slider-autoplay-pagination-bullet"><span></span></button>`,
          renderActiveBullet: () =>
            `<button class="slider-autoplay-pagination-bullet is-active"><span></span></button>`,
        }),
      )

      el.value.addEventListener('animationend', onAnimationEnd)
    }
  }

  /**
   * We can rely on this event being fired once the bullet animation has ended
   * to go to the next slide. That way it's 100% in sync with actual autoplay.
   */
  function onAnimationEnd(e: AnimationEvent) {
    // Make sure this is the animation that has ended.
    if (e.animationName !== FLICKING_BULLET_ANIMATION_NAME) {
      return
    }

    next()
  }

  function withFlicking(cb: WithFlickingCallback) {
    if (flicking) {
      cb(flicking)
    }
  }

  function moveTo(index: number, duration?: number) {
    withFlicking((v) => v.moveTo(index, duration))
  }

  function next() {
    withFlicking((v) => {
      // Prevent error from happening when going to next slide when an
      // animation is currently running.
      if (v.animating) {
        v.stopAnimation()
      }

      v.next()
    })
  }

  function cleanup() {
    if (el.value) {
      el.value.removeEventListener('animationend', onAnimationEnd)
    }

    if (flicking) {
      if (persistKey.value && flicking.currentPanel) {
        currentIndexState.value[persistKey.value] = flicking.currentPanel.index
      }
      flicking.destroy()
    }

    flicking = null
    _initialisedForElement = null
  }

  onMounted(initFlicking)
  onBeforeUnmount(cleanup)

  watch(isEnabled, (enabled) => {
    if (enabled) {
      initFlicking()
    } else {
      cleanup()
    }
  })

  // Load the default CSS.
  await import('~/assets/css/dynamic/flicking.css')

  return {
    withFlicking,
    moveTo,
    next,
  }
}
